/**
 * ŠABLÓNA "template_1" – LANDSCAPE.
 * Pôvodný (doteraz jediný) dizajn obrazovky denného menu na šírku.
 * Registruje sa v ./dailyMenuTemplates.js pod id "template_1".
 */
import React, { useMemo, useState, useEffect } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { useRotatingLocale } from "./useRotatingLocale";
import { useDailyMenuScreenData, COUNTDOWN_INTERVAL_MS } from "app/hooks/dsok/useDailyMenuScreenData";
import Ticker from "./components/Ticker";
import { DailyMenuContentList, DailyMenuContentGrid } from "./content";
import ErrorView from "./ErrorView";
import "./DailyMenuScreen.css";

const TICKER_HEIGHT_PX = 48;
const ALLERGENS_BAR_HEIGHT_PX = 72;
const MEALS_PAGE_INTERVAL_MS = 5000;
/* Grid landscape: jedlá 4 v riadku, 2 riadky = 8 na stránku. Polievky 2 v riadku, max 2 riadky = 4 na stránku. Pri viac ako 8 jedlách / 4 polievkach ďalšia strana. */
const GRID_LANDSCAPE_SOUPS_PER_PAGE = 4;  /* 2 stĺpce × 2 riadky */
const GRID_LANDSCAPE_MEALS_PER_PAGE = 8;  /* 4 stĺpce × 2 riadky */

const DATE_TIME_LOCALE = "sk-SK";

function formatDateOnly(date) {
  try {
    const formatted = new Intl.DateTimeFormat(DATE_TIME_LOCALE, {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    }).format(date);
    return formatted.replace(/\s/g, "");
  } catch {
    return date.toLocaleDateString(DATE_TIME_LOCALE).replace(/\s/g, "");
  }
}

/* Odhad výšky: header ~70px, allergens ~56px, ticker ~48px, content padding ~64px => content ≈ vh - 238px */
function getResponsiveCounts(innerHeight, innerWidth) {
  const h = innerHeight || 1080;
  const w = innerWidth || 1920;
  const isNarrow = w < 900;
  const contentHeight = h - 238;
  const rowHeight = isNarrow ? 100 : 95;
  const maxRowsInContent = Math.max(2, Math.floor(contentHeight / rowHeight));
  let mealsPerPage;
  let maxSoups;
  if (h < 500) {
    mealsPerPage = 2;
    maxSoups = 1;
  } else if (h < 600) {
    mealsPerPage = 2;
    maxSoups = 1;
  } else if (h < 700) {
    mealsPerPage = 3;
    maxSoups = 1;
  } else if (h < 800) {
    mealsPerPage = 4;
    maxSoups = isNarrow ? 1 : 2;
  } else if (h < 920) {
    mealsPerPage = 5;
    maxSoups = 2;
  } else if (h < 1050) {
    mealsPerPage = 6;
    maxSoups = 2;
  } else {
    maxSoups = 2;
    mealsPerPage = Math.min(8, Math.max(6, Math.floor(maxRowsInContent) - maxSoups));
  }
  return { mealsPerPage, maxSoups };
}

const DailyMenuScreen = () => {
  const { customerCode, locationCode } = useParams();
  const [searchParams] = useSearchParams();
  const env = searchParams.get("env") ?? "";

  const {
    loading,
    error,
    refetch,
    logo,
    activeMenuType,
    defaultLocale,
    supportedLocales,
    rotateMs,
    soups,
    meals,
    getTitle,
    getAllergensLegend,
    getTickerText,
    showAllergens,
    showDescription,
    showPrices,
    productDisplayType,
    soupPlaceholderImage,
    mealPlaceholderImage,
  } = useDailyMenuScreenData({
    customerCode,
    locationCode,
    env,
    menuId: "default",
    orientation: "landscape",
  });

  const [locale] = useRotatingLocale(supportedLocales, rotateMs);

  const [timeTick, setTimeTick] = useState(0);
  const [mealPageIndex, setMealPageIndex] = useState(0);
  const [gridPageIndex, setGridPageIndex] = useState(0);
  const [windowSize, setWindowSize] = useState(() => ({
    width: typeof window !== "undefined" ? window.innerWidth : 1920,
    height: typeof window !== "undefined" ? window.innerHeight : 1080,
  }));

  useEffect(() => {
    const onResize = () =>
      setWindowSize({ width: window.innerWidth, height: window.innerHeight });
    window.addEventListener("resize", onResize);
    onResize();
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    setMealPageIndex(0);
    setGridPageIndex(0);
  }, [locale]);

  useEffect(() => {
    const id = setInterval(() => setTimeTick((t) => t + 1), 1000);
    return () => clearInterval(id);
  }, []);

  const { mealsPerPage } = useMemo(
    () => getResponsiveCounts(windowSize.height, windowSize.width),
    [windowSize.height, windowSize.width],
  );

  const title = useMemo(() => getTitle(locale), [getTitle, locale]);
  const allergensLegend = useMemo(
    () => getAllergensLegend(locale),
    [getAllergensLegend, locale],
  );
  const tickerText = useMemo(
    () => getTickerText(locale),
    [getTickerText, locale],
  );
  const visibleSoups = soups;
  const mealPageCount = Math.max(1, Math.ceil(meals.length / mealsPerPage));
  const mealPageStart = mealPageIndex * mealsPerPage;
  const visibleMeals = meals.slice(mealPageStart, mealPageStart + mealsPerPage);

  /* Grid landscape: stránkovanie polievok len ak je viac ako 4 polievky; inak všetky polievky, stránkujú sa len jedlá */
  const gridSoupsPaging = soups.length > GRID_LANDSCAPE_SOUPS_PER_PAGE;
  const gridSoupsPageCount = gridSoupsPaging
    ? Math.max(1, Math.ceil(soups.length / GRID_LANDSCAPE_SOUPS_PER_PAGE))
    : 1;
  const gridMealsPageCount = Math.max(1, Math.ceil(meals.length / GRID_LANDSCAPE_MEALS_PER_PAGE));
  const gridPageCount = gridSoupsPaging
    ? Math.max(gridSoupsPageCount, gridMealsPageCount)
    : gridMealsPageCount;
  const gridSoupsStart = gridSoupsPaging ? gridPageIndex * GRID_LANDSCAPE_SOUPS_PER_PAGE : 0;
  const gridMealsStart = gridPageIndex * GRID_LANDSCAPE_MEALS_PER_PAGE;
  const visibleGridSoups = gridSoupsPaging
    ? soups.slice(gridSoupsStart, gridSoupsStart + GRID_LANDSCAPE_SOUPS_PER_PAGE)
    : soups.slice(0, GRID_LANDSCAPE_SOUPS_PER_PAGE);
  const visibleGridMeals = meals.slice(gridMealsStart, gridMealsStart + GRID_LANDSCAPE_MEALS_PER_PAGE);

  useEffect(() => {
    setMealPageIndex((i) =>
      mealPageCount <= 1 ? 0 : Math.min(i, mealPageCount - 1),
    );
  }, [mealsPerPage, mealPageCount]);

  useEffect(() => {
    if (mealPageCount <= 1) return undefined;
    const id = setInterval(() => {
      setMealPageIndex((i) => (i + 1) % mealPageCount);
    }, MEALS_PAGE_INTERVAL_MS);
    return () => clearInterval(id);
  }, [mealPageCount]);

  useEffect(() => {
    setGridPageIndex((i) =>
      gridPageCount <= 1 ? 0 : Math.min(i, gridPageCount - 1),
    );
  }, [gridPageCount]);

  useEffect(() => {
    if (gridPageCount <= 1) return undefined;
    const id = setInterval(() => {
      setGridPageIndex((i) => (i + 1) % gridPageCount);
    }, MEALS_PAGE_INTERVAL_MS);
    return () => clearInterval(id);
  }, [gridPageCount]);

  const now = useMemo(() => new Date(), [timeTick]); // eslint-disable-line react-hooks/exhaustive-deps -- timeTick updates every second for live clock
  const dateOnlyStr = formatDateOnly(now);
  const hours = String(now.getHours()).padStart(2, "0");
  const minutes = String(now.getMinutes()).padStart(2, "0");
  const timeStr = `${hours}:${minutes}`;
  const headerLeftText = dateOnlyStr ? `${title} ${dateOnlyStr}` : title;
  const hasTicker = Boolean(tickerText && tickerText.trim());
  const showAllergensBar = showAllergens && allergensLegend.length > 0;

  if (loading) {
    return (
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", width: "100%", height: "100vh", margin: 0, background: "#000", color: "#888", fontSize: "1.5rem" }}>
        {logo ? (
          <img src={logo} alt="" style={{ maxWidth: "40vw", maxHeight: "40vh", objectFit: "contain" }} />
        ) : (
          "Loading…"
        )}
      </div>
    );
  }
  if (error) {
    return (
      <ErrorView
        error={error}
        onRetry={refetch}
        countdownIntervalMs={COUNTDOWN_INTERVAL_MS}
        className="daily-menu-screen"
      />
    );
  }

  const contentProps = {
    locale,
    defaultLocale,
    placeholderImageSoup: soupPlaceholderImage,
    placeholderImageMeal: mealPlaceholderImage,
    soldOutLabel: "",
    classNamePrefix: "daily-menu-screen",
    showDescription,
    showPrices,
  };

  return (
    <div className="daily-menu-screen">
      <header
        className="daily-menu-screen__header"
        style={hasTicker ? { marginBottom: 0 } : undefined}
      >
        <h1 className="daily-menu-screen__title">{headerLeftText}</h1>
        {activeMenuType?.label && (
          <span className="daily-menu-screen__menu-type">
            {activeMenuType.label}
          </span>
        )}
        <span className="daily-menu-screen__time">{timeStr}</span>
      </header>
      <div
        className="daily-menu-screen__content"
        style={
          hasTicker || showAllergensBar
            ? {
                paddingBottom:
                  (showAllergensBar ? ALLERGENS_BAR_HEIGHT_PX : 0) +
                  (hasTicker ? TICKER_HEIGHT_PX : 0) +
                  16,
              }
            : undefined
        }
      >
        {productDisplayType === "grid" ? (
          <DailyMenuContentGrid
            orientation="landscape"
            soups={visibleGridSoups}
            meals={visibleGridMeals}
            soupsSingleRow={visibleGridSoups.length >= 1 && visibleGridSoups.length <= 2}
            mealsSingleRow={visibleGridMeals.length >= 1 && visibleGridMeals.length <= 4}
            {...contentProps}
          />
        ) : (
          <DailyMenuContentList
            orientation="landscape"
            soups={visibleSoups}
            meals={visibleMeals}
            {...contentProps}
          />
        )}
      </div>
      {showAllergensBar && (
        <div
          className="daily-menu-screen__allergens-bar"
          style={hasTicker ? { bottom: TICKER_HEIGHT_PX } : undefined}
        >
          <div className="daily-menu-screen__allergens-bar-inner">
            {[...allergensLegend]
              .sort((a, b) => (a?.number ?? 0) - (b?.number ?? 0))
              .map((row, index, arr) => (
                <span
                  key={row?.number ?? row?.text ?? index}
                  className="daily-menu-screen__allergens-bar-item"
                >
                  {row?.number != null && (
                    <span className="daily-menu-screen__allergens-bar-num">
                      {row.number}
                    </span>
                  )}
                  {row?.number != null && row?.text != null && " – "}
                  {row?.text != null && (
                    <span className="daily-menu-screen__allergens-bar-text">
                      {row.text}
                    </span>
                  )}
                  {index < arr.length - 1 && (
                    <span className="daily-menu-screen__allergens-bar-sep">
                      {" | "}
                    </span>
                  )}
                </span>
              ))}
          </div>
        </div>
      )}
      {hasTicker && (
        <div
          className="daily-menu-screen__ticker"
          style={{ height: TICKER_HEIGHT_PX }}
        >
          <Ticker text={tickerText} />
        </div>
      )}
    </div>
  );
};

export default DailyMenuScreen;
